import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useSubmitSelfReflection } from "@/hooks/use-quarterly-reviews";
import { FormError } from "@/components/shared/form-error";
import type { QuarterlyReview } from "@/types/entities";

const selfReflectionSchema = z.object({
  self_reflection: z.string().min(1, "Self-reflection is required").max(10000),
});

type SelfReflectionFormData = z.infer<typeof selfReflectionSchema>;

interface SelfReflectionFormProps {
  review: QuarterlyReview;
  canEdit: boolean;
}

export function SelfReflectionForm({ review, canEdit }: SelfReflectionFormProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const submitReflection = useSubmitSelfReflection(review.id);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<SelfReflectionFormData>({
    resolver: zodResolver(selfReflectionSchema),
    defaultValues: { self_reflection: review.self_reflection ?? "" },
  });

  const onSubmit = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirm = () => {
    const data = getValues();
    submitReflection.mutate(data.self_reflection, {
      onSuccess: () => setConfirmDialogOpen(false),
    });
  };

  const isSubmitted = review.status !== "pending_self_reflection";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Self-Reflection</CardTitle>
        </CardHeader>
        <CardContent>
          {isSubmitted ? (
            <p className="whitespace-pre-wrap text-sm">
              {review.self_reflection ?? "No self-reflection submitted"}
            </p>
          ) : canEdit ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="self-reflection">
                  Reflect on your performance this quarter
                </Label>
                <Textarea
                  id="self-reflection"
                  placeholder="Describe your accomplishments, challenges, and areas for growth..."
                  rows={8}
                  {...register("self_reflection")}
                />
                <FormError message={errors.self_reflection?.message} />
              </div>
              <Button type="submit" disabled={submitReflection.isPending}>
                {submitReflection.isPending ? "Submitting..." : "Submit Self-Reflection"}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))] italic">
              Awaiting employee self-reflection
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Self-Reflection</DialogTitle>
            <DialogDescription>
              Once submitted, your self-reflection cannot be changed. Your supervisor
              will be able to provide feedback.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={submitReflection.isPending}>
              {submitReflection.isPending ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
